// Gestión de usuarios (CRUD) — solo admin. Listado + alta + edición de
// rol/estado, reseteo de contraseña y regeneración de API key de técnicos.
import { useEffect, useState, type FormEvent } from 'react';
import {
  api,
  ApiError,
  ROL_LABEL,
  fechaCorta,
  type UsuarioAdmin,
  type Usuario,
} from '../api.js';

const ROLES: Usuario['rol'][] = ['admin', 'gestor_oficina', 'tecnico'];

export function Usuarios() {
  const [usuarios, setUsuarios] = useState<UsuarioAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [mostrarAlta, setMostrarAlta] = useState(false);
  // API key recién generada que mostramos una sola vez (id -> key).
  const [keyMostrada, setKeyMostrada] = useState<{ id: number; key: string } | null>(null);

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const { usuarios } = await api.listUsuarios();
      setUsuarios(usuarios);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudieron cargar los usuarios.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void cargar();
  }, []);

  const flash = (msg: string) => {
    setAviso(msg);
    window.setTimeout(() => setAviso(null), 4000);
  };

  const onCambiarRol = async (u: UsuarioAdmin, rol: Usuario['rol']) => {
    if (rol === u.rol) return;
    try {
      await api.editarUsuario(u.id, { rol });
      flash(`Rol de ${u.nombre} actualizado.`);
      await cargar();
    } catch (err) {
      flash(err instanceof ApiError ? err.message : 'No se pudo cambiar el rol.');
    }
  };

  const onToggleActivo = async (u: UsuarioAdmin) => {
    try {
      await api.editarUsuario(u.id, { activo: !u.activo });
      flash(`${u.nombre} ${u.activo ? 'desactivado' : 'activado'}.`);
      await cargar();
    } catch (err) {
      flash(err instanceof ApiError ? err.message : 'No se pudo cambiar el estado.');
    }
  };

  const onResetPassword = async (u: UsuarioAdmin) => {
    const nueva = window.prompt(`Nueva contraseña para ${u.nombre} (mín. 6 caracteres):`);
    if (nueva === null) return;
    if (nueva.length < 6) {
      flash('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    try {
      await api.resetPassword(u.id, nueva);
      flash(`Contraseña de ${u.nombre} actualizada.`);
    } catch (err) {
      flash(err instanceof ApiError ? err.message : 'No se pudo resetear la contraseña.');
    }
  };

  const onRegenerarKey = async (u: UsuarioAdmin) => {
    if (!window.confirm(`Regenerar la API key de ${u.nombre}? La anterior dejará de funcionar.`)) return;
    try {
      const { apiKey } = await api.regenerarApiKey(u.id);
      setKeyMostrada({ id: u.id, key: apiKey });
      await cargar();
    } catch (err) {
      flash(err instanceof ApiError ? err.message : 'No se pudo regenerar la API key.');
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <h2>Usuarios</h2>
        <button className="btn-primary inline" onClick={() => setMostrarAlta((v) => !v)}>
          {mostrarAlta ? 'Cancelar' : 'Nuevo usuario'}
        </button>
      </div>

      {aviso && <div className="aviso-box">{aviso}</div>}

      {mostrarAlta && (
        <AltaUsuario
          onCreado={(nombre, apiKey, id) => {
            setMostrarAlta(false);
            if (apiKey && id) setKeyMostrada({ id, key: apiKey });
            flash(`Usuario ${nombre} creado.`);
            void cargar();
          }}
        />
      )}

      {keyMostrada && (
        <div className="apikey-box">
          <strong>API key generada</strong> (cópiala ahora, no se vuelve a mostrar):
          <code>{keyMostrada.key}</code>
          <button className="btn-ghost" onClick={() => setKeyMostrada(null)}>
            Cerrar
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading">Cargando…</div>
      ) : error ? (
        <div className="error-box">{error}</div>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Alta</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u) => (
              <tr key={u.id} className={u.activo ? '' : 'fila-inactiva'}>
                <td>{u.nombre}</td>
                <td>{u.email}</td>
                <td>
                  <select
                    value={u.rol}
                    onChange={(e) => void onCambiarRol(u, e.target.value as Usuario['rol'])}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROL_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <span className={`badge ${u.activo ? 'estado-aceptada' : 'estado-rechazada'}`}>
                    {u.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td>{fechaCorta(u.createdAt)}</td>
                <td className="acciones">
                  <button className="btn-ghost sm" onClick={() => void onToggleActivo(u)}>
                    {u.activo ? 'Desactivar' : 'Activar'}
                  </button>
                  <button className="btn-ghost sm" onClick={() => void onResetPassword(u)}>
                    Contraseña
                  </button>
                  {u.rol === 'tecnico' && (
                    <button className="btn-ghost sm" onClick={() => void onRegenerarKey(u)}>
                      API key
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AltaUsuario({
  onCreado,
}: {
  onCreado: (nombre: string, apiKey: string | null, id: number | null) => void;
}) {
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<Usuario['rol']>('tecnico');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await api.crearUsuario({ email, nombre, rol, password });
      onCreado(r.nombre, r.apiKey, r.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo crear el usuario.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="alta-card" onSubmit={onSubmit}>
      <div className="alta-grid">
        <label className="field">
          <span>Nombre</span>
          <input value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        </label>
        <label className="field">
          <span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field">
          <span>Rol</span>
          <select value={rol} onChange={(e) => setRol(e.target.value as Usuario['rol'])}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROL_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Contraseña (mín. 6)</span>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </label>
      </div>
      {rol === 'tecnico' && (
        <p className="hint">Se generará una API key para la app Android del técnico.</p>
      )}
      {error && <div className="error-box">{error}</div>}
      <button className="btn-primary inline" type="submit" disabled={busy}>
        {busy ? 'Creando…' : 'Crear usuario'}
      </button>
    </form>
  );
}
